<script setup>
import { computed } from 'vue';
import ProductParagraph from 'vue-elements/product-info/ProductParagraph.vue';
import { useEnergyComparisonStore } from '../../stores/energyComparison';
import PricingTable from 'vue-elements/product-info/PricingTable.vue';
import PricingTableHeader from 'vue-elements/product-info/PricingTableHeader.vue';
import PricingTableRow from 'vue-elements/product-info/PricingTableRow.vue';
import icon from 'vue-elements/icon/Icon.vue';
import { format, parse } from 'date-fns';
const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
});


const comparisonStore = useEnergyComparisonStore();

const showFixedRateEndDate = computed(() => !!props.product.fixed_rate_end_date);

const fixedEndDate = computed(() => {
    const endDate = parse(props.product.fixed_end_date, 'yyyy-MM-dd', new Date());
    return format(endDate, 'dd-MM-yyyy');
});

const isPriceCapped = computed(() => {
    let hasPriceCap = false;
    props.product.price_calculation.electricity_specification?.forEach((item) => {
        if (item.type === 'price_cap_electricity_single' || item.type === 'price_cap_electricity_normal' || item.type === 'price_cap_electricity_offpeak') {
            hasPriceCap = true;
        }
    });
    props.product.price_calculation.gas_specification?.forEach((item) => {
        if (item.type === 'price_cap_gas') {
            hasPriceCap = true;
        }
    });
    return hasPriceCap;
});

const hasVVVIncentive = computed(() => {
  return comparisonStore.hasVVVIncentive && props.product.available_orderline;
});

</script>

<template>
    <div class="product-details">
        <pricing-table class="details-rows">
            <pricing-table-header>
                <strong>Contract</strong>
            </pricing-table-header>

            <pricing-table-row v-if="showFixedRateEndDate">
                <template #description>
                    Tarieven t/m {{ parsedFixedRateEndDate }}
                </template>
                <template #value>
                    Vast
                </template>
            </pricing-table-row>
            <pricing-table-row v-if="showFixedRateEndDate">
                <template #description>
                    Tarieven na {{ parsedFixedRateEndDate }}
                </template>
                <template #value>
                    Variabel
                </template>
            </pricing-table-row>
            <pricing-table-row v-if="!showFixedRateEndDate">
                <template #description>
                    Tarieven
                </template>
                <template
                    v-if="product.contract_duration === 1 && product.tariff_type !== 'dynamic' && product.tariff_type !== 'dynamic_gas_fixed'"
                    #value>
                    Variabel
                </template>
                <template v-else-if="product.tariff_type === 'dynamic'" #value>
                    Dynamisch
                </template>
                <template v-else-if="product.tariff_type === 'dynamic_gas_fixed'" #value>
                    Dynamische stroomprijs, vaste gasprijs
                </template>
                <template v-else-if="product.tariff_type === 'fixed' && product.fixed_end_date" #value>
                    Vaste tarieven
                </template>
                <template v-else #value>
                    {{ product.contract_duration_text }} vast
                </template>
            </pricing-table-row>

            <pricing-table-row>
                <template #description>
                    Contractduur
                </template>
                <template #value>
                    {{ product.contract_duration_text }}
                </template>
            </pricing-table-row>

            <pricing-table-row v-if="product.offer_new_customers_only">
                <template #description>
                    Voorwaarden
                </template>
                <template #value>
                    <div style="text-align: right">
                        Alleen voor nieuwe klanten
                    </div>
                </template>
            </pricing-table-row>
            <pricing-table-row v-if="product.offer_existing_customers_only">
                <template #description>
                    Voorwaarden
                </template>
                <template #value>
                    Alleen voor bestaande klanten
                </template>
            </pricing-table-row>

            <product-paragraph v-if="product.terms && product.terms.length > 0" custom-class="terms">
                <template #heading>
                    Voorwaarden
                </template>
                <template #value>
                    <ul>
                        <li v-for="term in product.terms" :key="term.name" class="term-item">
                            <a target="_blank" rel="nofollow" :href="term.url">
                                <icon icon="x-fa-file-alt" />
                                {{ term.name }}
                            </a>
                        </li>
                    </ul>
                </template>
            </product-paragraph>
        </pricing-table>
        <div class="package-discount">
            <product-paragraph v-if="product.total_discount > 0">
                <template #heading>
                    Korting
                </template>
                <template #value>
                    Als je dit {{ product.provider_name }} contract afsluit krijg je
                    <strong>{{ toCurrencyShort(product.total_discount) }} euro korting</strong> op je energiecontract.
                    Deze korting wordt verrekend met de jaarafrekening aan het einde van het jaar.
                </template>
            </product-paragraph>
            <product-paragraph v-else-if="product.tariff_type === 'variable'">
                <template #heading>
                    Variabel contract
                </template>
                <template #value>
                    Dit is een variabel contract.
                    De tarieven voor stroom
                    <template v-if="product.gas_included">
                        en gas
                    </template>
                    kunnen meerdere keren per
                    jaar veranderen.
                </template>
            </product-paragraph>
            <product-paragraph v-else-if="product.tariff_type === 'dynamic'">
                <template #heading>
                    Dynamisch energiecontract
                </template>
                <template #value>
                    Dit is een dynamisch energiecontract.
                    Stroom
                    <template v-if="product.gas_included">
                        en gas
                    </template>
                    wordt geleverd tegen de marktprijs die iedere dag (of zelfs ieder uur) verandert.
                    <br>
                    <br>
                    De maandprijs die je ziet is een indicatie op basis van de actuele marktprijzen.
                </template>
            </product-paragraph>
            <product-paragraph v-else-if="product.tariff_type === 'dynamic_gas_fixed'">
                <template #heading>
                    Dynamisch stroomprijs met vaste gasprijs
                </template>
                <template #value>
                    Dit is een dynamisch energiecontract.
                    Stroom wordt geleverd tegen de marktprijs die iedere dag (of zelfs ieder uur) verandert.
                    Het gastarief zet je wel voor {{ product.contract_duration_text }} vast.
                    <br>
                    <br>
                    De maandprijs die je ziet is een indicatie op basis van de actuele marktprijzen.
                </template>
            </product-paragraph>
            <product-paragraph v-if="showFixedRateEndDate && product.tariff_type === 'variable'">
                <template #heading>
                    Tarieven t/m {{ parsedFixedRateEndDate }}
                </template>
                <template #value>
                    Dit is een variabel contract. De tarieven voor stroom
                    <template v-if="product.gas_included">
                        en gas
                    </template>
                    die je nu ziet betaal je tot en met <strong>{{ parsedFixedRateEndDate }}</strong> en kunnen daarna
                    iedere maand veranderen.
                    <br><br>
                    De tarieven fluctueren op basis van de actuele inkoopprijzen. {{ product.provider_name }}
                    informeert je altijd tijdig per mail over de nieuwe tarieven.
                </template>
            </product-paragraph>
            <product-paragraph
                v-else-if="product.total_discount == 0 && product.offer_text_long == null && product.tariff_type !== 'dynamic' && product.tariff_type !== 'dynamic_gas_fixed'">
                <template #heading>
                    Vaste lage prijs
                </template>
                <template #value>
                    Met dit contract heb je een vaste lage prijs.
                    De tarieven voor stroom
                    <template v-if="product.gas_included">
                        en gas
                    </template>
                    <template v-if="product.fixed_end_date">
                      zet je vast tot en met {{ fixedEndDate }}.
                    </template>
                    <template v-if="!product.fixed_end_date">
                      zet je voor {{ product.contract_duration_text }} vast.
                    </template>
                </template>
            </product-paragraph>
            <product-paragraph v-if="product.provider_description != null && product.provider_description.length > 0">
                <template #heading>
                    Over {{ product.provider_name }}
                </template>
                <template #value>
                    <span v-html="product.provider_description" />
                </template>
            </product-paragraph>
            <!-- Probably improve this to be part of BAAS -->
            <product-paragraph v-if="['budget-energie', 'united-consumers', 'mega'].includes(product.provider)">
                <template #heading>
                    Winnaar Beste Keuze Award 2025
                </template>
                <template #value>
                    <span v-if="product.provider === 'mega' && comparisonStore.filters.electricityGenerated">
                        Mega heeft in 2025 de Beste Keuze Award in de categorie ‘zonnepanelen’ gewonnen. Ze stonden het vaakst in de top 3 van onze energievergelijker o.b.v. totale kosten, inclusief terugleverkosten en -vergoeding.                    </span>
                    <span v-else-if="product.provider === 'budget-energie'">
                        Budget Energie heeft in 2025 de Beste Keuze Award in de categorie 'laagste prijs' gewonnen.
                        Volgens ons prijsonderzoek had Budget Energie in 2025 consistent lage tarieven en goede
                        aanbiedingen.
                    </span>
                    <span v-else-if="product.provider === 'united-consumers'">
                        UnitedConsumers heeft in 2025 de Beste Keuze Award in de categorie
                        'klanttevredenheid' gewonnen. Op basis van onze vijf klantcriteria, waaronder klantreviews, wist UnitedConsumers de hoogste score te behalen.
                    </span>
                </template>
            </product-paragraph>
            <!-- VVV Incentive-->
            <product-paragraph v-if="hasVVVIncentive">
                <template #heading>
                    Gratis VVV cadeaubon
                </template>
                <template #value>
                    <span>
                        Stap je via Keuze.nl over naar {{ product.provider_name }}? Dan krijg je een VVV cadeaubon van €10! Je ontvangt deze binnen 4-6 weken per e-mail na bevestiging van je overstap.<a href="/actievoorwaarden" target="_blank">Lees alle actievoorwaarden.</a>
                    </span>
                </template>
            </product-paragraph>
            <product-paragraph v-if="product.total_discount == 0 && product.gift_list != null">
                <template #heading>
                    Kies je cadeau
                </template>
                <template #value>
                    Met dit contract kun je kiezen voor verschillende cadeaus, zoals:
                    <ul>
                        <li v-for="gift in product.gift_list.split('\n')" :key="gift">
                            {{ gift }}
                        </li>
                    </ul>
                </template>
            </product-paragraph>
            <product-paragraph v-if="isPriceCapped">
                <template #heading>
                    Inclusief prijsplafond
                </template>
                <template #value>
                    In de prijsberekening van dit contract hebben we rekening gehouden met het prijsplafond van de
                    overheid.<br><br>
                    In 2023 betaal je voor de eerste 2900 kWh stroom en de eerste 1200 m³ gas nooit meer dan het
                    maximumtarief van € 0,40 per kWh stroom en € 1,45 euro per m³ gas. Boven dit verbruik gelden de
                    normale
                    tarieven van dit energiecontract.
                </template>
            </product-paragraph>
        </div>
    </div>
</template>